import { type ReactNode } from "react";
import { useLocation } from "wouter";
import type { ThemeRegion } from "@workspace/theme-engine";
import { useThemeEngine } from "./context";
import { RegionRenderer } from "./RegionRenderer";

/**
 * Map a storefront pathname to the theme region that drives it. Only the main
 * page surfaces are mapped; everything else (account, wishlist, blog, search…)
 * returns null so the routed page renders unchanged.
 */
export function regionForPath(pathname: string): ThemeRegion | null {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p === "/") return "home";
  if (/^\/(product|p)\//.test(p)) return "product";
  if (/^\/(category|categories|collections?|shop|products)(\/|$)/.test(p)) return "listing";
  if (p === "/cart") return "cart";
  if (/^\/(page|pages)\//.test(p)) return "customPage";
  return null;
}

/**
 * Wraps the routed page content and, when a tenant has activated an engine
 * theme whose layout drives the current page's region, composes that region
 * from the theme package (the region's content-anchor component delegates back
 * to `children`). When the engine is off or the route is unmapped, it renders
 * `children` unchanged — so default stores are byte-for-byte identical.
 */
export function PageRegionRenderer({ children }: { children: ReactNode }) {
  const theme = useThemeEngine();
  const [pathname] = useLocation();
  if (!theme) return <>{children}</>;
  const region = regionForPath(pathname);
  if (!region) return <>{children}</>;
  return <RegionRenderer region={region} pageContent={children} fallback={children} />;
}
